import type { MetricRole } from '@/domain/metrics/contracts';
import type { MetricsEngineResult } from '../selectors';
import type {
  ReverseEdgeRequirement,
  ReverseRequirement,
  ReverseResult,
  SimulationGraphInput,
  SimulationTarget,
  TargetStatus,
} from '../contracts';
import type { SimulationWarning } from '../warnings';
import {
  ambiguousReverseMerge,
  cycleRequiresReverseBoundary,
  missingAverageTicket,
  missingConversion,
  zeroConversionUnreachable,
} from '../warnings';
import { ceilRequirement } from './rounding';
import { reverseThroughEdge } from './split';
import { resolveReverseMergeShares, type MergeEdgeShare } from './merge';
import { isReverseCycle } from './cycle';
import { computeGap, computeTargetStatus } from './target-status';

type StartMetric = 'entries' | 'exits';

/**
 * SPEC-009 §19-35/plan §15-24 — pure reverse-planning orchestrator. Never
 * duplicates a SPEC-008 formula: every "current" value comes straight from
 * `metricsResult` (built by `runMetricsEngine`), and every backward step
 * reuses `reverseThroughEdge`/`resolveReverseMergeShares`/`isReverseCycle`.
 *
 * `edgeReverseShares` is the per-scenario `edgeOverrides[edgeId].reverseShare`
 * map (SIM-REQ-007/008) — kept outside `SimulationGraphInput` because it is a
 * planning-only assumption, not an operational graph field.
 */
export function calculateReverseRequirements(
  graph: SimulationGraphInput,
  metricsResult: MetricsEngineResult,
  target: SimulationTarget,
  edgeReverseShares?: Readonly<Record<string, number>>,
): ReverseResult {
  const warnings: SimulationWarning[] = [];
  const unresolved: string[] = [];
  const requiredNodeMetrics = new Map<string, ReverseRequirement[]>();
  const requiredEdgeMetrics = new Map<string, ReverseEdgeRequirement>();

  const incomingByTarget = new Map<string, typeof graph.edges[number][]>();
  for (const node of graph.nodes) incomingByTarget.set(node.id, []);
  for (const edge of graph.edges) incomingByTarget.get(edge.target)?.push(edge);

  const pushNodeRequirement = (req: ReverseRequirement) => {
    const list = requiredNodeMetrics.get(req.nodeId) ?? [];
    list.push(req);
    requiredNodeMetrics.set(req.nodeId, list);
  };

  const currentNodeValue = (nodeId: string, metric: StartMetric): number | null => {
    const result = metricsResult.propagation.nodeResults.get(nodeId);
    return result ? ((result[metric].effectiveValue as number | null) ?? null) : null;
  };

  const buildRequirement = (
    nodeId: string,
    metric: StartMetric,
    required: number | null,
    rawRequired: number | null,
    opts: { unreachable?: boolean; unresolved?: boolean; rounding?: 'ceiling' | 'none' } = {},
  ): ReverseRequirement => {
    const current = currentNodeValue(nodeId, metric);
    const status: TargetStatus = computeTargetStatus({
      required,
      current,
      unreachable: opts.unreachable,
      unresolved: opts.unresolved,
    });
    return {
      nodeId,
      metric,
      rawRequired,
      required,
      current,
      gap: computeGap(required, current),
      status,
      rounding: opts.rounding,
    };
  };

  function traverseFromExits(nodeId: string, requiredExits: number, visitedPath: ReadonlySet<string>): void {
    if (isReverseCycle(visitedPath, nodeId)) {
      warnings.push(cycleRequiresReverseBoundary([nodeId]));
      unresolved.push(nodeId);
      return;
    }
    const path = new Set(visitedPath);
    path.add(nodeId);

    pushNodeRequirement(buildRequirement(nodeId, 'exits', requiredExits, requiredExits));

    const incomingEdges = incomingByTarget.get(nodeId) ?? [];
    if (incomingEdges.length === 0) {
      // Top of the funnel: nothing further upstream to backsolve, so this
      // node's own entries<-exits conversion is never needed (and never
      // reported as unresolved just because it happens to be unknown).
      return;
    }

    const nodeConversion = metricsResult.propagation.nodeResults.get(nodeId)?.conversion.effectiveValue as
      | number
      | null
      | undefined;
    const through = reverseThroughEdge(requiredExits, nodeConversion ?? null);

    if (through.status === 'unresolved') {
      warnings.push(missingConversion([nodeId]));
      pushNodeRequirement(buildRequirement(nodeId, 'entries', null, null, { unresolved: true }));
      unresolved.push(nodeId);
      return;
    }
    if (through.status === 'unreachable') {
      warnings.push(zeroConversionUnreachable([nodeId]));
      pushNodeRequirement(buildRequirement(nodeId, 'entries', null, requiredExits, { unreachable: true }));
      unresolved.push(nodeId);
      return;
    }

    const requiredEntries = through.requiredSourceExits!;
    pushNodeRequirement(
      buildRequirement(nodeId, 'entries', requiredEntries, through.rounding?.rawRequired ?? requiredEntries, {
        rounding: through.rounding?.rounding,
      }),
    );

    traverseIncoming(nodeId, requiredEntries, path);
  }

  function traverseFromEntries(nodeId: string, requiredEntries: number, visitedPath: ReadonlySet<string>): void {
    if (isReverseCycle(visitedPath, nodeId)) {
      warnings.push(cycleRequiresReverseBoundary([nodeId]));
      unresolved.push(nodeId);
      return;
    }
    const path = new Set(visitedPath);
    path.add(nodeId);

    pushNodeRequirement(buildRequirement(nodeId, 'entries', requiredEntries, requiredEntries));
    traverseIncoming(nodeId, requiredEntries, path);
  }

  function traverseIncoming(nodeId: string, requiredEntries: number, path: ReadonlySet<string>): void {
    const incomingEdges = incomingByTarget.get(nodeId) ?? [];
    if (incomingEdges.length === 0) return;

    if (incomingEdges.length === 1) {
      const edge = incomingEdges[0]!;
      resolveEdge(edge.id, edge.source, requiredEntries, path);
      return;
    }

    const shareInputs: MergeEdgeShare[] = incomingEdges.map((edge) => ({
      edgeId: edge.id,
      reverseShare: edgeReverseShares?.[edge.id],
    }));
    const resolution = resolveReverseMergeShares(shareInputs);
    if (!resolution.resolved) {
      warnings.push(ambiguousReverseMerge(incomingEdges.map((edge) => edge.id)));
      unresolved.push(nodeId);
      return;
    }
    for (const edge of incomingEdges) {
      const share = resolution.shares.get(edge.id) ?? 0;
      resolveEdge(edge.id, edge.source, requiredEntries * share, path);
    }
  }

  function resolveEdge(edgeId: string, sourceNodeId: string, requiredFlow: number, path: ReadonlySet<string>): void {
    const edgeConversion = metricsResult.propagation.edgeResults.get(edgeId)?.conversion.effectiveValue as
      | number
      | null
      | undefined;
    const currentFlow = metricsResult.propagation.edgeResults.get(edgeId)?.flow.effectiveValue as
      | number
      | null
      | undefined;
    const through = reverseThroughEdge(requiredFlow, edgeConversion ?? null);

    const status: TargetStatus = computeTargetStatus({
      required: through.status === 'ok' ? through.requiredSourceExits : null,
      current: currentFlow ?? null,
      unreachable: through.status === 'unreachable',
      unresolved: through.status === 'unresolved',
    });
    requiredEdgeMetrics.set(edgeId, {
      edgeId,
      requiredFlow: through.status === 'ok' ? through.requiredSourceExits : null,
      currentFlow: currentFlow ?? null,
      status,
    });

    if (through.status === 'unresolved') {
      warnings.push(missingConversion([edgeId]));
      unresolved.push(edgeId);
      return;
    }
    if (through.status === 'unreachable') {
      warnings.push(zeroConversionUnreachable([edgeId]));
      unresolved.push(edgeId);
      return;
    }
    traverseFromExits(sourceNodeId, through.requiredSourceExits!, path);
  }

  // --- Resolve the target node/metric (spec §21-28) -------------------------

  const explanation: ReverseResult['explanation'] = { targetNodeId: null, targetMetric: null };

  const resolveClientsNode = (): string | null => {
    if (target.nodeId) return target.nodeId;
    const candidates = graph.nodes.filter((node) => node.roles.includes('new_customer' as MetricRole));
    return candidates.length === 1 ? candidates[0]!.id : null;
  };

  if (target.metric === 'entries' || target.metric === 'exits') {
    const nodeId = target.nodeId ?? null;
    if (!nodeId) {
      return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved: ['target'], warnings, explanation };
    }
    explanation.targetNodeId = nodeId;
    explanation.targetMetric = target.metric;
    const rounding = ceilRequirement(target.value);
    if (target.metric === 'exits') {
      traverseFromExits(nodeId, rounding.roundedRequired, new Set());
    } else {
      traverseFromEntries(nodeId, rounding.roundedRequired, new Set());
    }
    return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved, warnings, explanation };
  }

  if (target.metric === 'clients') {
    const nodeId = resolveClientsNode();
    if (!nodeId) {
      return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved: ['target'], warnings, explanation };
    }
    explanation.targetNodeId = nodeId;
    explanation.targetMetric = 'exits';
    const rounding = ceilRequirement(target.value);
    traverseFromExits(nodeId, rounding.roundedRequired, new Set());
    return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved, warnings, explanation };
  }

  // target.metric === 'revenue'
  const nodeId = resolveClientsNode();
  if (!nodeId) {
    return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved: ['target'], warnings, explanation };
  }
  const averageTicket = metricsResult.propagation.nodeResults.get(nodeId)?.averageTicket.effectiveValue as
    | number
    | null
    | undefined;
  if (averageTicket === null || averageTicket === undefined || averageTicket <= 0) {
    warnings.push(missingAverageTicket([nodeId]));
    return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved: [nodeId], warnings, explanation };
  }
  explanation.targetNodeId = nodeId;
  explanation.targetMetric = 'exits';
  const requiredClients = ceilRequirement(target.value / averageTicket);
  explanation.derivedFromRevenue = { requiredClients: requiredClients.roundedRequired, averageTicket };
  traverseFromExits(nodeId, requiredClients.roundedRequired, new Set());
  return { target, requiredNodeMetrics, requiredEdgeMetrics, unresolved, warnings, explanation };
}
